import React, { useState } from "react";
import {
  Box,
  VStack,
  Text,
  HStack,
  IconButton,
  Collapse,
  Link,
  Badge
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { InsightsProto } from "build-ts/analysis";
import ReactMarkdown from "react-markdown";
import { PlanRunProto, AgentRunProto, ScenarioResultProto_ScenarioStatus, ScenarioResultProto_ReferencedTreeNodes } from "build-ts/schedule";
import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import { Link as RouterLink } from "react-router-dom";

// Component to display insights from CommentaryProto
interface InsightsCardProps {
  agentOutput?: AgentRunProto;
  planRun?: PlanRunProto;
  insights?: InsightsProto[];
}

// Common ReactMarkdown components configuration.
const markdownComponents = {
  ul: ({node, ...props}: any) => (
    <ul style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
  ),
  ol: ({node, ...props}: any) => (
    <ol style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
  ),
  li: ({node, ...props}: any) => (
    <li style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }} {...props} />
  ),
  p: ({node, ...props}: any) => (
    <p style={{ fontSize: "0.875rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
  ),
  h1: ({node, ...props}: any) => (
    <h1 style={{ fontSize: "1.15rem", fontWeight: "bold", margin: "1rem 0" }} {...props} />
  ),
  h2: ({node, ...props}: any) => (
    <h2 style={{ fontSize: "1.05rem", fontWeight: "bold", margin: "0.875rem 0" }} {...props} />
  ),
  h3: ({node, ...props}: any) => (
    <h3 style={{ fontSize: "0.95rem", fontWeight: "bold", margin: "0.75rem 0" }} {...props} />
  ),
  h4: ({node, ...props}: any) => (
    <h4 style={{ fontSize: "0.85rem", fontWeight: "bold", margin: "0.625rem 0" }} {...props} />
  ),
};

// Common component for displaying referenced tree nodes.
const ReferencedTreeNodes: React.FC<{ 
  treeNodes: ScenarioResultProto_ReferencedTreeNodes[], 
  getTreeName: (treeId: string) => string 
}> = ({ treeNodes, getTreeName }) => {
  if (!treeNodes || treeNodes.length === 0) return null;
  
  return (
    <Box mb={1}>
      <Text fontWeight="semibold" fontSize="sm" mb={1} color="gray.800" textAlign="left">
        Referenced Tree Nodes:
      </Text>
      <VStack spacing={2} align="stretch">
        {treeNodes?.map((treeNode, treeIndex) => (
          <Box 
            key={treeIndex} 
            bg="gray.50" 
            p={1} 
            borderRadius="md" 
            border="1px solid" 
            borderColor="gray.200"
            mb={1}
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1} textAlign="left">
              {getTreeName(treeNode?.tree_id || "") || "Unknown Tree"}
              {treeNode?.node_ids && treeNode?.node_ids.length > 0 && (
                <Text as="span" fontWeight="normal" color="gray.600" ml={2} mb={1}>
                  ({treeNode?.node_ids?.join(", ") || ""})
                </Text>
              )}
            </Text>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

const InsightsCard: React.FC<InsightsCardProps> = ({ insights, agentOutput, planRun }) => {
  const [isQAExpanded, setIsQAExpanded] = useState<boolean>(false);
  const [isAgentOutputExpanded, setIsAgentOutputExpanded] = useState<boolean>(false);
  const [isContextualizedOutputExpanded, setIsContextualizedOutputExpanded] = useState<boolean>(false);
  const [isScenariosExpanded, setIsScenariosExpanded] = useState<boolean>(false);
  const [expandedScenarios, setExpandedScenarios] = useState<Set<number>>(new Set());
  const [isSynthesisResultExpanded, setIsSynthesisResultExpanded] = useState<boolean>(false);
  const [isContextualizationResultExpanded, setIsContextualizationResultExpanded] = useState<boolean>(false);

  // Toggle functions
  const toggleQAExpansion = () => {
    setIsQAExpanded(!isQAExpanded);
  };

  const toggleAgentOutputExpansion = () => {
    setIsAgentOutputExpanded(!isAgentOutputExpanded);
  };

  const toggleContextualizedOutputExpansion = () => {
    setIsContextualizedOutputExpanded(!isContextualizedOutputExpanded);
  };

  const toggleScenariosExpansion = () => {
    setIsScenariosExpanded(!isScenariosExpanded);
  };

  const toggleScenarioExpansion = (scenarioId: number) => {
    const newExpanded = new Set(expandedScenarios);
    if (newExpanded.has(scenarioId)) {
      newExpanded.delete(scenarioId);
    } else {
      newExpanded.add(scenarioId);
    }
    setExpandedScenarios(newExpanded);
  };

  const toggleSynthesisResultExpansion = () => {
    setIsSynthesisResultExpanded(!isSynthesisResultExpanded);
  };

  const toggleContextualizationResultExpansion = () => {
    setIsContextualizationResultExpanded(!isContextualizationResultExpanded);
  };

  // Helper function to get scenario status info
  const getScenarioStatusInfo = (status: ScenarioResultProto_ScenarioStatus) => {
    switch (status) {
      case ScenarioResultProto_ScenarioStatus.VALID:
        return { color: "green", label: "Valid" };
      case ScenarioResultProto_ScenarioStatus.INVALID:
        return { color: "red", label: "Invalid" };
      case ScenarioResultProto_ScenarioStatus.WARNING:
        return { color: "orange", label: "Warning" };
      case ScenarioResultProto_ScenarioStatus.ERROR:
        return { color: "red", label: "Error" };
      default:
        return { color: "gray", label: "Unknown" };
    }
  };

  // Helper function to get tree name. If tree is not found, return "Unknown Tree".
  const getTreeName = (treeId: string) => {
    const tree = agentOutput?.tree_headers?.find((tree) => tree.id === treeId);
    return tree?.name || "Unknown Tree";
  };

  if (!insights && !agentOutput && !planRun) {
    return (
      <Text fontSize="sm" color="gray.500" mb={0}>
        No insights available
      </Text>
    );
  }

  return (
    <VStack spacing={1} align="stretch">
      {insights && insights?.length > 0 && (
        <Box>
          <HStack 
            spacing={2} 
            cursor="pointer" 
            onClick={toggleQAExpansion}
            _hover={{ bg: "gray.50" }}
            p={2}
            borderRadius="md"
            mb={2}
          >
            <IconButton
              aria-label="Toggle Questions & Answers"
              icon={isQAExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                toggleQAExpansion();
              }}
            />
            <Text fontWeight="semibold" fontSize="sm" mb={0} cursor="pointer">
              Questions & Answers: ({insights?.length})
            </Text>
          </HStack>
          
          <Collapse in={isQAExpanded} animateOpacity>
            <VStack spacing={2} align="stretch">
              {insights?.map((insight, index) => (
                <Box
                  key={index}
                  p={3}
                  borderWidth={1}
                  borderRadius="md"
                >
                  <Text fontWeight="medium" fontSize="sm" mb={1} align="left">
                    Q: {insight?.question}
                  </Text>
                  {insight?.answer && (
                    <Text fontSize="sm" color="gray.600" align="left">
                      A: 
                      <ReactMarkdown components={markdownComponents}>
                      {insight?.answer}
                    </ReactMarkdown>
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          </Collapse>
        </Box>
      )}
      {agentOutput && (
        <>
          {/* Tree Headers Display */}
          {agentOutput?.tree_headers && agentOutput?.tree_headers.length > 0 && (
            <Box mb={0} textAlign="left" alignItems="left">
              <Text fontSize="sm" fontWeight="medium" mb={0}>
                Analysis tree generated: {" "}
                {agentOutput?.tree_headers?.map((header, index) => (
                  <span key={header.id || index}>
                    <Link
                      as={RouterLink}
                      to={`/analyze?metadataId=${header?.id}&metadataType=${metadataTypeEnumToJSON(MetadataTypeEnum.ANALYSIS_TREE)}`}
                      color="purple.600"
                      textDecoration="underline"
                      _hover={{
                        color: "purple.700",
                        textDecoration: "none",
                      }}
                      isExternal
                    >
                      {header?.name}
                    </Link>
                    {index < agentOutput?.tree_headers?.length - 1 && ", "}
                  </span>
                ))}
              </Text>
            </Box>
          )}

          {/* Scenario Results */}
          {agentOutput?.scenario_results && agentOutput?.scenario_results.length > 0 && (
            <Box mb={1}>
              <HStack 
                spacing={2} 
                cursor="pointer" 
                onClick={toggleScenariosExpansion}
                _hover={{ bg: "gray.50" }}
                p={1}
                borderRadius="md"
                mb={1}
              >
                <IconButton
                  aria-label="Toggle Scenario Results"
                  icon={isScenariosExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleScenariosExpansion();
                  }}
                />
                <Text fontWeight="semibold" fontSize="sm" mb={0} cursor="pointer">
                  Scenario Results: ({agentOutput?.scenario_results?.length || 0})
                </Text>
              </HStack>
              
                <Collapse in={isScenariosExpanded} animateOpacity>
                <VStack spacing={2} align="stretch" ml={4}>
                  {agentOutput?.scenario_results?.map((scenario, index) => {
                    const scenarioId = scenario?.scenario_id || index;
                    const isExpanded = expandedScenarios.has(scenarioId);
                    const statusInfo = getScenarioStatusInfo(scenario?.status || ScenarioResultProto_ScenarioStatus.INVALID);
                    const isErrorStatus = scenario?.status === ScenarioResultProto_ScenarioStatus.ERROR;
                    
                    return (
                      <Box
                        key={scenarioId}
                        borderWidth={1}
                        borderRadius="md"
                        bg="gray.25"
                      >
                        <HStack 
                          spacing={1} 
                          cursor={isErrorStatus ? "default" : "pointer"}
                          onClick={isErrorStatus ? undefined : () => toggleScenarioExpansion(scenarioId)}
                          _hover={isErrorStatus ? {} : { bg: "gray.50" }}
                          p={2}
                          borderRadius="sm"
                        >
                          {!isErrorStatus && (
                            <IconButton
                              aria-label={`Toggle Scenario ${index + 1}`}
                              icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                              size="xs"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleScenarioExpansion(scenarioId);
                              }}
                            />
                          )}
                          {isErrorStatus && (
                            <Box w={6} h={6} /> // Spacer to maintain alignment
                          )}
                          <Text fontWeight="medium" fontSize="sm" mb={0}>
                            Scenario {index + 1}:
                          </Text>
                          <Badge colorScheme={statusInfo.color} size="xs">
                            {statusInfo.label}
                          </Badge>
                          {isErrorStatus && (
                            <Text fontSize="xs" color="gray.600" ml={2} mb={0}>
                              (No details available)
                            </Text>
                          )}
                        </HStack>
                        
                        {!isErrorStatus && (
                          <Collapse in={isExpanded} animateOpacity>
                            <VStack spacing={2} align="stretch" mt={1} ml={6} mr={2}>
                              {scenario?.findings && (
                                <Box>
                                  <Text fontWeight="semibold" fontSize="sm" mb={1} color="gray.800" textAlign="left">
                                    Findings:
                                  </Text>
                                  <Box 
                                    pl={3} 
                                    borderLeft="3px solid" 
                                    borderColor="gray.300"
                                    bg="gray.50"
                                    p={1}
                                    borderRadius="md"
                                  >
                                    <Text fontSize="sm" color="gray.700" lineHeight="1.5" textAlign="left" mb={1}>
                                      {scenario?.findings || ""}
                                    </Text>
                                  </Box>
                                </Box>
                              )}
                              
                              <ReferencedTreeNodes 
                                treeNodes={scenario?.referenced_tree_nodes || []} 
                                getTreeName={getTreeName} 
                              />
                            </VStack>
                          </Collapse>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </Collapse>
            </Box>
          )}

          {/* Synthesis Result */}
          {agentOutput?.synthesis_result && agentOutput?.synthesis_result.output_string && (
            <Box mb={1}>
              <HStack 
                spacing={2} 
                cursor="pointer" 
                onClick={toggleSynthesisResultExpansion}
                _hover={{ bg: "gray.50" }}
                p={1}
                borderRadius="md"
                mb={1}
              >
                <IconButton
                  aria-label="Toggle Synthesis Result"
                  icon={isSynthesisResultExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSynthesisResultExpansion();
                  }}
                />
                <Text fontWeight="semibold" fontSize="sm" mb={0} cursor="pointer">
                  Synthesis Result: (Notify: {agentOutput?.synthesis_result?.notify ? "True" : "False"})
                </Text>
              </HStack>
              
              <Collapse in={isSynthesisResultExpanded} animateOpacity>
                <VStack spacing={3} align="stretch" mt={3} ml={4} mr={2}>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" mb={2} color="gray.800" textAlign="left">
                      Output:
                    </Text>
                    <Box 
                      pl={3} 
                      borderLeft="3px solid" 
                      borderColor="gray.300"
                      bg="gray.50"
                      p={3}
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color="gray.700" lineHeight="1.5" textAlign="left">
                        <ReactMarkdown components={markdownComponents}>
                          {agentOutput?.synthesis_result?.output_string || ""}
                        </ReactMarkdown>
                      </Text>
                    </Box>
                  </Box>
                  
                  <ReferencedTreeNodes 
                    treeNodes={agentOutput?.synthesis_result?.referenced_tree_nodes || []} 
                    getTreeName={getTreeName} 
                  />
                </VStack>
              </Collapse>
            </Box>
          )}

          {/* Contextualization Result */}
          {agentOutput?.contextualisation_result && agentOutput?.contextualisation_result.output_string && (
            <Box mb={1}>
              <HStack 
                spacing={2} 
                cursor="pointer" 
                onClick={toggleContextualizationResultExpansion}
                _hover={{ bg: "gray.50" }}
                p={1}
                borderRadius="md"
                mb={1}
              >
                <IconButton
                  aria-label="Toggle Contextualization Result"
                  icon={isContextualizationResultExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleContextualizationResultExpansion();
                  }}
                />
                <Text fontWeight="semibold" fontSize="sm" mb={0} cursor="pointer">
                  Contextualization Result: (Notify: {agentOutput?.contextualisation_result?.notify ? "True" : "False"})
                </Text>
              </HStack>
              
              <Collapse in={isContextualizationResultExpanded} animateOpacity>
                <VStack spacing={3} align="stretch" mt={3} ml={4} mr={2}>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" mb={2} color="gray.800" textAlign="left">
                      Output:
                    </Text>
                    <Box 
                      pl={3} 
                      borderLeft="3px solid" 
                      borderColor="gray.300"
                      bg="gray.50"
                      p={3}
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color="gray.700" lineHeight="1.5" textAlign="left">
                        <ReactMarkdown components={markdownComponents}>
                          {agentOutput?.contextualisation_result?.output_string || ""}
                        </ReactMarkdown>
                      </Text>
                    </Box>
                  </Box>
                  
                  <ReferencedTreeNodes 
                    treeNodes={agentOutput?.contextualisation_result?.referenced_tree_nodes || []} 
                    getTreeName={getTreeName} 
                  />
                </VStack>
              </Collapse>
            </Box>
          )}

          {/* Agent Output */}
          {agentOutput?.agent_output && (
            <Box mb={1}>
          <HStack 
            spacing={2} 
            cursor="pointer" 
            onClick={toggleAgentOutputExpansion}
            _hover={{ bg: "gray.50" }}
                p={1}
            borderRadius="md"
                mb={1}
           >
            <IconButton
              aria-label="Toggle Agent Output"
                  icon={isAgentOutputExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                toggleAgentOutputExpansion();
              }}
            />
                  <Text fontWeight="semibold" fontSize="sm" mb={0} cursor="pointer">
                    Agent Output
                  </Text>
          </HStack>
          
          <Collapse in={isAgentOutputExpanded} animateOpacity>
            <Text fontSize="sm" align="left">
                   <ReactMarkdown components={markdownComponents}>
                      {agentOutput?.agent_output || "No agent output available"}
              </ReactMarkdown>
            </Text>
          </Collapse>
        </Box>
          )}

          {/* Contextualized Output */}
        {agentOutput?.contextualized_output && (
          <Box mb={1}>
            <HStack 
              spacing={2} 
              cursor="pointer" 
              onClick={toggleContextualizedOutputExpansion}
              _hover={{ bg: "gray.50" }}
              p={1}
              borderRadius="md"
              mb={1}
            >
              <IconButton
                  aria-label="Toggle Contextualized Output"
                  icon={isContextualizedOutputExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                size="xs"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleContextualizedOutputExpansion();
                }}
              />
                <Text fontWeight="semibold" fontSize="sm" mb={0} cursor="pointer">
                  Contextualized Output
                </Text>
            </HStack>
            
            <Collapse in={isContextualizedOutputExpanded} animateOpacity>
              <Text fontSize="sm" align="left">
                <ReactMarkdown components={markdownComponents}>
                  {agentOutput?.contextualized_output || "No contextualized output available"}
                </ReactMarkdown>
              </Text>
            </Collapse>
          </Box>
        )}
        </>
      )}  
    </VStack>
  );
};

export default InsightsCard;